import React, { FC, useCallback, useState, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { IFormField, IOption, OptionValue } from 'modules/core/types';
import { useUncontrolledInput } from 'modules/core/hooks';

import { CheckboxGroupUI, CheckBox, Input, LabelWrapper, Label, LenderNameTooltip } from './styles';

export interface ICheckboxGroup extends IFormField {
  options?: IOption[];
  columnCount: number;
  maxSelectedOptions: number;
}

interface ICheckboxItem {
  title: string;
  value: OptionValue;
  checked?: boolean;
  disabled?: boolean;
  onChange: (event: any) => void;
}

const CheckboxItem: FC<ICheckboxItem> = ({ title, value, checked = false, disabled = false, onChange }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);

  const handleLabelRef = (element: HTMLLabelElement | null) => {
    if (element) setIsOverflowed(element.scrollWidth > element.clientWidth);
  };

  return (
    <CheckBox>
      <Input
        className="form-check-input"
        type="checkbox"
        id={String(value)}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <LabelWrapper>
        <Label
          ref={handleLabelRef}
          htmlFor={String(value)}
          disabled={disabled}
          {...(isOverflowed && {
            'data-tooltip-id': `tooltip-${value}`,
            'data-tooltip-content': title,
          })}
        >
          {title}
        </Label>
      </LabelWrapper>
      {isOverflowed && (
        <LenderNameTooltip id={`tooltip-${value}`} place="bottom-start" delayShow={200} delayHide={50} />
      )}
    </CheckBox>
  );
};

export const CheckboxGroup: FC<ICheckboxGroup> = ({
  name,
  options = [],
  changeCallback,
  columnCount = 1,
  maxSelectedOptions = 0,
}) => {
  const { controlField, setValue, getValues, fieldValue } = useUncontrolledInput(name);
  const rowCount = useMemo(() => Math.ceil(options.length / columnCount), [options.length, columnCount]);

  const renderCheckboxes = useCallback(() => {
    const isMaxSelected = maxSelectedOptions > 0 && fieldValue.length >= maxSelectedOptions;

    const handleItemChange = (event: any) => {
      const values = getValues(name);
      const changedValue = event.target.value;

      if (event.target.checked && !values.includes(changedValue)) {
        values.push(changedValue);
      } else if (!event.target.checked && values.includes(changedValue)) {
        const index = values.indexOf(changedValue);

        values.splice(index, 1);
      }

      setValue(name, values);

      if (changeCallback) {
        changeCallback(event);
      }
    };

    return (
      <CheckboxGroupUI $columnCount={columnCount} $rowCount={rowCount}>
        {options.map(({ title, value }) => {
          const isChecked = fieldValue.includes(value);

          return (
            <CheckboxItem
              key={value}
              title={title}
              value={value}
              checked={isChecked}
              disabled={isMaxSelected && !isChecked}
              onChange={handleItemChange}
            />
          );
        })}
      </CheckboxGroupUI>
    );
  }, [maxSelectedOptions, fieldValue, columnCount, rowCount, options, getValues, name, setValue, changeCallback]);

  return <Controller render={renderCheckboxes} control={controlField} name={name} />;
};
